import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { hasModule, firstAllowedRoute } from "@/lib/auth";
import { SettingsMatrixClient } from "@/features/warehouse/SettingsClient";
import { WarehousePicker, useWarehouseChoice } from "@/features/warehouse/WarehouseParts";

// Warehouse → Instellingen, the matrix (docs/design_handoff_wms/README.md §4.3). Reading is
// wms_masterdata; changing is for admins until O3 is decided — on the API regardless.
export const Route = createFileRoute("/_shell/warehouse/settings/")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "wms_masterdata") && !hasModule(context.session, "wms_masterdata_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): { warehouse?: string } => ({
    warehouse: typeof search.warehouse === "string" && search.warehouse ? search.warehouse : undefined,
  }),
  component: SettingsScreen,
});

function SettingsScreen() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const choice = useWarehouseChoice(search.warehouse, (code) => navigate({ search: { warehouse: code }, replace: true }));
  const warehouse = choice.current;

  return (
    <div className="wl-page" data-screen-label="Back-office Warehouse Settings">
      {!warehouse ? (
        <p className="t-body-sm t-muted">{choice.isLoading ? t("common.loading") : t("warehouse.noWarehouses")}</p>
      ) : (
        <SettingsMatrixClient
          warehouse={warehouse}
          picker={<WarehousePicker warehouses={choice.warehouses} current={warehouse} onChoose={choice.choose} />}
          canEdit={session.roles.includes("admin")}
          canEditOutbound={session.roles.includes("admin") || hasModule(session, "wms_outbound_write")}
          onOpenOwner={(ownerId) => navigate({ to: "/warehouse/settings/$ownerId", params: { ownerId }, search: { warehouse: warehouse.code } })}
        />
      )}
    </div>
  );
}
